import {ReactNode, useEffect, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {caronaApi} from '../../../services/CaronaApi.service'
import {AssistantApi} from '../../../services/AssistantApi.service'
import {AtaDetalhe, DocumentoBloco, JustificativaOut, RespostasEntrevista} from '../types'
import {ESFERA_LABEL, fmtDate, rotuloRequisito, rotuloStatus} from '../ui'
import DicasLoading from './DicasLoading'

interface Props {
    justificativa: JustificativaOut
    ata: AtaDetalhe | null
    respostas: RespostasEntrevista
    evidencias: Record<string, string>
    aderenteNome: string
    autoIa: boolean
    onVoltarEntrevista: () => void
    onNovaBusca: () => void
}

function Pend({children}: {children: ReactNode}) {
    return <span className="pendente">[PENDENTE: {children}]</span>
}

/** "slug" amigável p/ nome de arquivo: sem acento, minúsculo, só a-z0-9 e hífen. */
function slug(s: string, max = 40): string {
    const base = (s || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    return base.slice(0, max).replace(/-+$/g, '')
}

/** Lê o documento (editável) do DOM e monta os blocos para o backend gerar o .docx. */
function blocosDoDom(root: HTMLElement): {titulo: string; subtitulo: string; blocos: DocumentoBloco[]} {
    let titulo = 'Justificativa de Adesão a Ata de Registro de Preços'
    let subtitulo = ''
    const blocos: DocumentoBloco[] = []
    Array.from(root.children).forEach((el) => {
        const tag = el.tagName
        const txt = (el.textContent || '').replace(/\s+/g, ' ').trim()
        if (el.classList.contains('doc-title')) {
            if (txt) titulo = txt
        } else if (el.classList.contains('doc-sub')) {
            subtitulo = txt
        } else if (tag === 'H2') {
            if (txt) blocos.push({nivel: 'h', texto: txt})
        } else if (tag === 'TABLE') {
            el.querySelectorAll('tr').forEach((tr) => {
                const cells = Array.from(tr.querySelectorAll('th,td')).map((c) => (c.textContent || '').trim())
                const linha = cells.filter(Boolean).join('  ·  ')
                if (linha) blocos.push({nivel: 'p', texto: linha})
            })
        } else if (txt) {
            blocos.push({nivel: 'p', texto: txt})
        }
    })
    return {titulo, subtitulo, blocos}
}

export default function JustificativaView(
    {justificativa, ata, respostas, evidencias, aderenteNome, autoIa, onVoltarEntrevista, onNovaBusca}: Props
) {
    const apta = justificativa.status === 'APTA'
    const comRisco = justificativa.status === 'COM_RISCO'
    const bannerCls = apta ? 'status-banner--apta' : comRisco ? 'status-banner--risco' : 'status-banner--erro'

    const e = respostas
    const itens = ata?.itens || []
    const temEv = (id: string) => Boolean((evidencias[id] || '').trim())
    const vigente = ata ? ['VIGENTE', 'A_VENCER'].includes(ata.status_vigencia) : false

    const docRef = useRef<HTMLDivElement>(null)
    const [baixando, setBaixando] = useState<'' | 'docx' | 'pdf'>('')
    const [melhorando, setMelhorando] = useState(false)
    const [feedbackModal, setFeedbackModal] = useState(false)
    const [feedbackText, setFeedbackText] = useState('')
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
    // IA automática na primeira versão (só em geração nova; no histórico não re-roda).
    const [iaInicial, setIaInicial] = useState(autoIa)
    const iaRodou = useRef(false)

    useEffect(() => {
        if (!autoIa || iaRodou.current) return
        iaRodou.current = true
        gerarComIa(true).finally(() => setIaInicial(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Nome de arquivo personalizado p/ a justificativa: objeto + nº da ata (sem extensão).
    const nomeArquivo = (() => {
        const partes = ['justificativa-adesao']
        if (ata?.objeto) partes.push(slug(ata.objeto))
        if (ata?.numero_ata) partes.push('ata-' + slug(ata.numero_ata))
        if (partes.length === 1) partes.push(new Date().toISOString().slice(0, 10))
        return partes.join('-')
    })()

    async function baixar(formato: 'docx' | 'pdf') {
        if (!docRef.current) return
        const {titulo, subtitulo, blocos} = blocosDoDom(docRef.current) // captura edições antes do re-render
        setBaixando(formato)
        try {
            const blob = await caronaApi.gerarDocumento({titulo, subtitulo, blocos}, formato)
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${nomeArquivo}.${formato}`
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
        } catch (err: any) {
            toast.error(`Falha ao gerar o ${formato.toUpperCase()} (${err?.message || 'erro'}).`)
        } finally {
            setBaixando('')
        }
    }

    async function enviarFeedback() {
        if (!feedbackText.trim()) return
        setFeedbackSubmitting(true)
        try {
            const assistantApi = new AssistantApi()
            await assistantApi.reportModule({
                object_id: justificativa.id,
                module: 'smart_minutes',
                title: ata?.objeto || 'Justificativa de adesão',
                feedback: feedbackText,
            })
            toast.success('Obrigado pelo feedback.')
            setFeedbackModal(false)
            setFeedbackText('')
        } catch (err: any) {
            toast.error(`Falha ao enviar feedback (${err?.message || 'erro'}).`)
        } finally {
            setFeedbackSubmitting(false)
        }
    }

    /** PDF no próprio navegador (instantâneo, sem servidor): imprime só o documento
     * via @media print → o usuário escolhe "Salvar como PDF". Captura também as edições. */
    function imprimirPdf() {
        const tituloOriginal = document.title
        document.title = nomeArquivo
        window.print()
        setTimeout(() => { document.title = tituloOriginal }, 800)
    }

    /** "WOW": envia o rascunho à IA (n8n) e substitui o texto do documento pelo
     * reescrito — mantendo fatos, [PENDENTE] e citações. O gestor revê e edita. */
    async function gerarComIa(silent = false) {
        if (!docRef.current) return
        const {titulo, subtitulo, blocos} = blocosDoDom(docRef.current)
        setMelhorando(true)
        try {
            const melhorados = await caronaApi.melhorarJustificativa({titulo, subtitulo, blocos})
            const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            const realce = (s: string) => esc(s).replace(/\[PENDENTE:[^\]]*\]/g, (m) => `<span class="pendente">${m}</span>`)
            docRef.current.innerHTML = [
                `<div class="doc-title">${esc(titulo)}</div>`,
                subtitulo ? `<div class="doc-sub">${esc(subtitulo)}</div>` : '',
                ...melhorados.map((b) => b.nivel === 'h'
                    ? `<h2 class="doc-h">${esc(b.texto)}</h2>`
                    : `<p class="doc-p">${realce(b.texto)}</p>`),
            ].join('')
            if (!silent) toast.success('Texto reescrito pela Licito.Guru IA. Revise e edite antes de baixar.')
        } catch (err: any) {
            if (!silent) toast.error(`Licito.Guru IA indisponível (${err?.message || 'erro'}).`)
        } finally {
            setMelhorando(false)
        }
    }

    return (
        <div>
            <header className="page-header">
                <div className="crumb">
                    <a onClick={onNovaBusca}>Ata Inteligente</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <a onClick={onVoltarEntrevista}>Entrevista</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <span>Justificativa gerada</span>
                </div>
                <div className="header-row">
                    <h1 className="lg-h2">Justificativa de adesão</h1>
                    <button className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onVoltarEntrevista}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                        Voltar à entrevista
                    </button>
                </div>
            </header>

            <div className="page-content" style={{maxWidth: 920}}>
                <div className={`status-banner ${bannerCls}`}>
                    <span className="status-banner__icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            dangerouslySetInnerHTML={{__html: apta ? '<path d="M20 6 9 17l-5-5"/>' : '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>'}} />
                    </span>
                    <div>
                        <div className="status-banner__title">
                            {apta ? 'Justificativa APTA' : comRisco ? `Justificativa COM RISCO — ${justificativa.riscos.length} pendência(s)` : 'Erro ao gerar'}
                        </div>
                        <div className="status-banner__desc">
                            {apta
                                ? 'Todos os requisitos obrigatórios foram satisfeitos. Documento pronto para o parecer jurídico e o controle prévio.'
                                : comRisco
                                ? 'O status APTA é recusado enquanto houver requisito sem evidência. A minuta foi gerada com marcações [PENDENTE] — sem fingir completude.'
                                : 'Não foi possível concluir o processamento. Tente novamente.'}
                        </div>
                    </div>
                </div>

                {justificativa.riscos.length > 0 && (
                    <div className="detail-panel" style={{marginBottom: 22}}>
                        <div className="detail-panel__title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--feedback-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            Relatório de pendências
                        </div>
                        <div className="risco-list">
                            {justificativa.riscos.map((r, i) => (
                                <div key={i} className="risco-item">
                                    <span className={`risco-item__sev sev--${(r.severidade || 'media').toLowerCase()}`}>{r.severidade}</span>
                                    <div>
                                        <div className="risco-item__req">{rotuloRequisito(r.requisito_id)} · {r.base_legal}</div>
                                        <div className="risco-item__msg">{r.mensagem}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ---- Documento editável (o mesmo conteúdo que vira o .docx) ---- */}
                <div className="doc-edit-hint" style={{justifyContent: 'space-between', alignItems: 'center'}}>
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                        Primeira versão <strong>redigida pela Licito.Guru IA</strong> a partir da entrevista — edite à vontade, ou refaça.
                    </span>
                    <button className="lg-btn lg-btn--brand lg-btn--sm" onClick={() => gerarComIa()} disabled={melhorando}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/><path d="M19 14l.7 1.8L21.5 16.5 19.7 17.2 19 19l-.7-1.8L16.5 16.5 18.3 15.8 19 14z"/></svg>
                        {melhorando ? 'Redigindo…' : 'Refazer com Licito.Guru IA'}
                    </button>
                </div>
                <div style={{position: 'relative'}}>
                    {(iaInicial || melhorando) && (
                        <div className="doc-ia-overlay"><DicasLoading titulo="Redigindo com Licito.Guru IA…" /></div>
                    )}
                <div className="doc-paper" ref={docRef} contentEditable suppressContentEditableWarning>
                    <div className="doc-title">Justificativa de Adesão a Ata de Registro de Preços</div>
                    <div className="doc-sub">{aderenteNome} · {ESFERA_LABEL[e.esfera_aderente]} · adesão por órgão não participante (carona)</div>

                    <h2 className="doc-h">1. Necessidade e motivação</h2>
                    <p className="doc-p">
                        {e.demanda_descricao.trim() || <Pend>descrever a demanda concreta do órgão</Pend>}{' '}
                        {e.demanda_prevista_pca ? 'A demanda está prevista no planejamento da contratação (PCA/DFD).' : <>A previsão no planejamento (PCA/DFD) não foi confirmada: <Pend>referência no PCA/DFD</Pend>.</>}
                        {e.alega_urgencia && (temEv('urgencia_desabastecimento')
                            ? <> Justifica-se a urgência (evidência: {evidencias['urgencia_desabastecimento']}).</>
                            : <> Há urgência alegada, mas <Pend>fundamento/evidência da urgência</Pend>.</>)}
                    </p>

                    <h2 className="doc-h">2. Fundamentação legal</h2>
                    <p className="doc-p">
                        A adesão encontra amparo nos arts. 82 a 86 da Lei nº 14.133/2021 e no Decreto nº 11.462/2023,
                        que regulamentam o Sistema de Registro de Preços e a contratação por órgão não participante,
                        observados os limites e vedações dos §§ 3º, 4º, 7º e 8º do art. 86 e a vigência do art. 84.
                    </p>

                    <h2 className="doc-h">3. Demonstração da vantajosidade</h2>
                    <p className="doc-p">
                        {e.declara_preco_compativel
                            ? <>O responsável declara que o preço registrado é compatível com o parâmetro de mercado (modo autônomo, v1). Recomenda-se, para maior segurança jurídica, pesquisa de preços própria e atualizada do órgão aderente, em observância ao TCU (Acórdãos 420/2018-Plenário e 1794/2023-1ª Câmara), que veda aferir a vantajosidade pela mera comparação com os preços da própria ata.</>
                            : <><Pend>vantajosidade não comprovada</Pend> — A demonstração da vantajosidade exige pesquisa de preços própria e atualizada (TCU 420/2018 e 1794/2023). A comparação restrita aos preços registrados na ata é insuficiente e não pode embasar a adesão.</>}
                    </p>

                    <h2 className="doc-h">4. Verificação dos limites quantitativos</h2>
                    <p className="doc-p">
                        Os quantitativos pretendidos respeitam o limite individual de 50% por item (art. 86, §3º), conforme tabela.
                        {' '}Saldo coletivo (200%, §4º): <strong>{ata?.saldo_status === 'DISPONIVEL' ? 'disponível' : ata?.saldo_status === 'ESGOTADO' ? 'esgotado' : 'indeterminado'}</strong> — confirme com o órgão gerenciador.
                    </p>
                    <table className="doc-table">
                        <thead><tr>
                            <th>Item</th><th>Descrição</th>
                            <th style={{textAlign: 'right'}}>Registrado</th>
                            <th style={{textAlign: 'right'}}>Teto 50%</th>
                            <th style={{textAlign: 'right'}}>Pretendido</th>
                            <th style={{textAlign: 'center'}}>OK</th>
                        </tr></thead>
                        <tbody>
                            {(() => {
                                // Só os itens que o gestor pretende adquirir (qtd > 0 na entrevista).
                                const pretendidos = itens.filter((it) => (e.quantidades_desejadas[String(it.numero_item)] || 0) > 0)
                                if (pretendidos.length === 0) {
                                    return (
                                        <tr><td colSpan={6} style={{color: 'var(--text-tertiary)'}}>
                                            <Pend>informe na entrevista as quantidades pretendidas por item</Pend>
                                        </td></tr>
                                    )
                                }
                                return pretendidos.map((it) => {
                                    const teto = Math.floor(it.qtd_registrada * 0.5)
                                    const q = e.quantidades_desejadas[String(it.numero_item)] || 0
                                    const okq = q <= teto
                                    return (
                                        <tr key={it.numero_item}>
                                            <td>{it.numero_item}</td>
                                            <td>{it.descricao}</td>
                                            <td style={{textAlign: 'right'}}>{it.qtd_registrada.toLocaleString('pt-BR')}</td>
                                            <td style={{textAlign: 'right'}}>{teto.toLocaleString('pt-BR')}</td>
                                            <td style={{textAlign: 'right'}}>{q.toLocaleString('pt-BR')}</td>
                                            <td style={{textAlign: 'center'}}>{okq ? '✓' : '✕ excede'}</td>
                                        </tr>
                                    )
                                })
                            })()}
                        </tbody>
                    </table>

                    <h2 className="doc-h">5. Vigência</h2>
                    <p className="doc-p">
                        A Ata de Registro de Preços nº {ata?.numero_ata || '—'}, gerenciada por {ata?.orgao}, encontra-se{' '}
                        {vigente ? <>vigente até {fmtDate(ata?.vigencia_fim)}</> : <Pend>ata fora de vigência</Pend>}, atendido o requisito de adesão a ata vigente (art. 84).
                    </p>

                    <h2 className="doc-h">6. Anuências</h2>
                    <p className="doc-p">
                        {temEv('anuencia_gerenciador') ? 'Consta a autorização do órgão gerenciador.' : <><Pend>autorização do órgão gerenciador</Pend>.</>}{' '}
                        {temEv('aceite_fornecedor') ? 'Consta o aceite do fornecedor registrado.' : <><Pend>aceite do fornecedor registrado</Pend>.</>}{' '}
                        {(!temEv('anuencia_gerenciador') || !temEv('aceite_fornecedor')) && <em>A presente justificativa é emitida como minuta condicionada à obtenção das anuências pendentes.</em>}
                    </p>

                    <h2 className="doc-h">7. Conclusão</h2>
                    <p className="doc-p">
                        {apta
                            ? 'Satisfeitos os requisitos legais e jurisprudenciais, a adesão à presente Ata de Registro de Preços mostra-se regular e vantajosa, recomendando-se o prosseguimento para parecer jurídico (art. 53) e publicidade no PNCP, que permanecem a cargo da autoridade competente.'
                            : <>Há requisitos pendentes de comprovação (vide relatório de pendências). Esta minuta <strong>não autoriza a adesão</strong>: as lacunas marcadas como <span className="pendente">[PENDENTE]</span> devem ser supridas com a evidência correspondente antes do encaminhamento ao parecer jurídico.</>}
                    </p>
                </div>
                </div>

                <div className="actions-bar">
                    <div className="match-score" style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>
                        Status: <strong>{rotuloStatus(justificativa.status)}</strong> · gerada em {fmtDate(new Date().toISOString().slice(0, 10))}
                    </div>
                    <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
                        <button className="lg-btn lg-btn--secondary" onClick={imprimirPdf}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                            Salvar em PDF
                        </button>
                        <button className="lg-btn lg-btn--primary" onClick={() => baixar('docx')} disabled={!!baixando}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            {baixando === 'docx' ? 'Gerando…' : 'Baixar .docx'}
                        </button>
                        <button className="lg-btn lg-btn--secondary" onClick={() => setFeedbackModal(true)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            Enviar Feedback
                        </button>
                    </div>
                </div>

                <div className="disclaimer">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <div>Este módulo <strong>instrui e documenta</strong>: não envia o pedido de adesão, não assina e não substitui o parecer jurídico (art. 53) nem a decisão da autoridade competente. Toda afirmação factual tem lastro nas respostas da entrevista — o documento não inventa datas, quantidades ou números de processo.</div>
                </div>
            </div>

            {feedbackModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15, 23, 42, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                    onClick={() => setFeedbackModal(false)}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 12,
                            padding: 24,
                            width: 460,
                            maxWidth: '90vw',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
                            <h3 style={{margin: 0, fontSize: 17}}>Enviar feedback</h3>
                            <button
                                className="lg-btn lg-btn--secondary lg-btn--sm"
                                onClick={() => setFeedbackModal(false)}
                                aria-label="Fechar"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>
                        <p style={{margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 14}}>
                            {ata?.objeto || 'Justificativa de adesão'}
                        </p>
                        <textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Descreva sua análise sobre esta justificativa..."
                            rows={5}
                            style={{
                                width: '100%',
                                padding: 10,
                                borderRadius: 8,
                                border: '1px solid var(--border-default, #ccc)',
                                fontFamily: 'inherit',
                                fontSize: 14,
                                resize: 'vertical',
                                boxSizing: 'border-box',
                            }}
                        />
                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16}}>
                            <button className="lg-btn lg-btn--secondary" onClick={() => setFeedbackModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className="lg-btn lg-btn--primary"
                                onClick={enviarFeedback}
                                disabled={!feedbackText.trim() || feedbackSubmitting}
                            >
                                {feedbackSubmitting ? 'Enviando…' : 'Enviar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
